import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { fetchMe, logout as apiLogout, attributeReferral } from "../lib/api";

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

// V2.0.40 — Referral attribution. The Landing page stashes a `?ref=`
// query param into sessionStorage as REF_STORAGE_KEY. After auth
// completes we POST it to /api/referrals/attribute (idempotent) so the
// referring user gets credited with a free Tier 1 unlock. We clear
// the key on success or hard failure so it can't double-fire on later
// sessions.
const REF_STORAGE_KEY = "blueprint:referral_id";

async function _maybeAttributeReferral() {
  try {
    const refId = sessionStorage.getItem(REF_STORAGE_KEY);
    if (!refId || !refId.startsWith("ar_")) return;
    await attributeReferral(refId);
  } catch (_) {
    // swallow — attribution is non-blocking; we'll retry next session
    return;
  }
  try { sessionStorage.removeItem(REF_STORAGE_KEY); } catch (err) { console.debug("[Auth] sessionStorage cleanup failed:", err); }
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const r = await fetchMe();
      setUser(r.data);
      // Fire-and-forget attribution as soon as we know the user is real.
      _maybeAttributeReferral();
    } catch (e) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const logout = async () => {
    try {
      await apiLogout();
    } catch (err) { console.debug("[Auth] logout request failed:", err); }
    setUser(null);
    window.location.href = "/";
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, refresh: checkAuth, logout }}>
      {children}
    </AuthContext.Provider>
  );
};
